import React, { useRef, useState, useEffect } from 'react';
import { assetUrl } from '@/utils/asset-url';
import classname from 'classnames';
import { ButtonType, ButtonVariant } from '@/componentsDS/Button/Button.type';
import useEllipsisCheck from '@/hooksDS/use-ellipsis-check/index';
import { IconSize, IconStyle } from '@/componentsDS/Icon/Icon.type';
import Checkbox from '@/componentsDS/Checkbox/Checkbox';
import { uniqueId, debounce } from 'lodash';
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';
import Cell from './Cell';
import PaginationControls from './PaginationControls';
import Button from '@/componentsDS/Button/Button';
import Icon from '@/componentsDS/Icon/Icon';
import Tooltip from '@/componentsDS/Tooltip';
import PropTypes from 'prop-types';


const RenderCheckBoxCell = ({ isHeader, row, leftFreeze, sortedData, selectedItems, setSelectedItems }) => {
	const style = {
		flexBasis: '56px',
		...(leftFreeze && { left: `0px` })
	};

	const cellClassName = classname('ds-table__cell', 'ds-table__cell--selectable', {
		'ds-table__header-cell': isHeader,
		'ds-table__cell--frozen-left': leftFreeze
	});

	// Exclude disabled items from the selection logic
	const selectableData = sortedData.filter((item) => !item.disabled);

	const areAllCurrentPageItemsSelected = selectableData.length > 0 && selectableData.every((item) => selectedItems.some((selectedItem) => selectedItem.id === item.id));

	const hasSelectableItems = selectableData.length > 0;

	const selectAllItems = () => {
		// Select all non-disabled items on the current page
		setSelectedItems((prevSelected) => {
			const selectedIds = new Set(prevSelected.map((selectedItem) => selectedItem.id));
			return [...prevSelected, ...selectableData.filter((item) => !selectedIds.has(item.id))];
		});
	};

	const deselectAllItems = () => {
		// Deselect all non-disabled items on the current page
		setSelectedItems((prevSelected) => {
			const selectableIds = new Set(selectableData.map((item) => item.id));
			return prevSelected.filter((selectedItem) => !selectableIds.has(selectedItem.id));
		});
	};

	const handleSelectAll = () => {
		selectAllItems();
	};

	const handleDeselectAll = () => {
		deselectAllItems();
	};

	const handleSelect = (row, checked) => {
		if (checked) {
			// Add the row to selected items
			setSelectedItems((prevSelected) => [...prevSelected, row]);
		} else {
			// Remove the row from selected items
			setSelectedItems((prevSelected) => prevSelected.filter((item) => item.id !== row.id));
		}
	};

	if (isHeader) {
		return (
			<div className={cellClassName} style={style}>
				<Checkbox id="select-all-checkbox" checked={areAllCurrentPageItemsSelected} disabled={!hasSelectableItems} onChange={(checked) => (checked ? handleSelectAll() : handleDeselectAll())} />

				<div className="ds-table__header-cell__right-line" />
			</div>
		);
	}

	if (!row) return null; // Safety check

	return (
		<div className={cellClassName} style={style}>
			<Checkbox id={`row-checkbox-${row.id}`} checked={selectedItems.some((item) => item.id === row.id)} disabled={row.disabled} onChange={(checked) => handleSelect(row, checked)} />
		</div>
	);
};

const Table = ({ headers, data, onSort, sortConfig, title, customGroupHeaderRender, commonGroupHeaderRender, isCollapsible, stickyOffset = 0, leftFreeze, rightFreeze, isSelectable, customSelectedText = 'item selected:', emptyMessage = 'No items added yet.', pagination, size, loading, additionalClasses, defaultSort, paginationOptions, informationHeaderRender, tableHelperText }) => {
	const [localSortedData, setLocalSortedData] = useState(data);
	const [localSortConfig, setLocalSortConfig] = useState({
		key: defaultSort ?? (headers.length > 0 ? headers[0].key : 'id'),
		direction: 'asc'
	});
	const [collapsed, setCollapsed] = useState(false);
	const [topBarHeight, setTopBarHeight] = useState(0);
	const [selectedItems, setSelectedItems] = useState([]);
	const [titleMaxWidth, setTitleMaxWidth] = useState(null);

	const topBarRef = useRef(null);
	const collapseButtonRef = useRef(null);
	const titleRef = useRef(null);
	const actionsRef = useRef(null);
	const observedDivRef = useRef(null);
	const resizingDelayTimer = useRef();


	// Refs for header cells
	const headerCellRefs = useRef([]);
	headers.forEach((_, index) => {
		headerCellRefs.current[index] = headerCellRefs.current[index] || React.createRef();
	});

	// const [cumulativeLeftWidths, setCumulativeLeftWidths] = useState([]);
	// const [cumulativeRightWidths, setCumulativeRightWidths] = useState([]);
	// Update cumulative widths on window resize

	const { isEllipsis, elementRefs } = useEllipsisCheck();

	useEffect(() => {
		setLocalSortedData(data);
		setPaginatedData(data);
	}, [data])

	// Increase the latency of the resize event to clean up the calculations
	useEffect(() => {
		const observedElement = observedDivRef.current;

		// Debounced width calculation for window resize
		const debouncedCalculateWidths = debounce(calculateWidths, 500);
		window.addEventListener('resize', debouncedCalculateWidths);

		// ResizeObserver for specific element changes
		const observer = new ResizeObserver(() => {
			clearTimeout(resizingDelayTimer.current);
			resizingDelayTimer.current = setTimeout(() => {
				if (observedDivRef.current) {
					calculateWidths();
				}
			}, 100);
		});

		// Observe the specified element
		if (observedDivRef.current) observer.observe(observedDivRef.current);

		function calculateWidths() {
			// The freeze logic is static for now; cumulative widths for the left and
			// right frozen columns are derived from the rendered header refs.

			// Calculate title max width based on top-bar, collapse-button, and actions widths

			const topBarWidth = topBarRef.current?.offsetWidth ?? 0;
			const collapseButtonWidth = collapsed ? 0 : (collapseButtonRef.current?.offsetWidth ?? 0);
			const actionsWidth = actionsRef.current?.offsetWidth ?? 0;
			const availableWidth = topBarWidth - collapseButtonWidth - actionsWidth; // 20px padding buffer

			setTitleMaxWidth(Math.max(availableWidth, 0));
		}

		// Initial calculation
		calculateWidths();

		//trigger resize event to calculate the width when header is rendered
		window.dispatchEvent(new Event('resize'));

		return () => {
			// Cleanup
			window.removeEventListener('resize', debouncedCalculateWidths);
			debouncedCalculateWidths.cancel();
			if (observer && observedElement) observer.unobserve(observedElement);

			clearTimeout(resizingDelayTimer.current);
		};
	}, [headers, collapsed]);

	// Measure the height of the top bar after it has been rendered
	useEffect(() => {
		if (topBarRef.current) {
			setTopBarHeight(topBarRef.current.offsetHeight);
		} else {
			setTopBarHeight(0);
		}
	}, [title, customGroupHeaderRender]);

	const currentSortConfig = onSort ? sortConfig : localSortConfig;

	// Handle sorting for data
	const handleSort = (key) => {
		let direction = 'asc';

		if (currentSortConfig && currentSortConfig.key === key && currentSortConfig.direction === 'asc') {
			direction = 'desc';
		}

		if (onSort) {
			// For API data, delegate sorting to the parent component
			onSort({ key, direction });
		} else {
			// For local data, sort internally
			const sortedArray = [...localSortedData].sort((a, b) => {
				const aValue = a[key];
				const bValue = b[key];
				if (aValue === null || bValue === null) return 0;
				if (aValue < bValue) return direction === 'asc' ? -1 : 1;
				if (aValue > bValue) return direction === 'asc' ? 1 : -1;
				return 0;
			});

			setLocalSortedData(sortedArray);
			setLocalSortConfig({ key, direction });
		}
	};

	useEffect(() => {
		if (!onSort) {
			handleSort(localSortConfig.key);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Determine if the top bar should be rendered
	const showTopBar = title != null || customGroupHeaderRender != null;

	// Calculate the cumulative offset for the header
	const additionalOffset = showTopBar ? topBarHeight : 0;
	const headerTopOffset = stickyOffset > 0 ? stickyOffset + additionalOffset : 0;

	const renderGroupHeader = () => {
		if (selectedItems.length > 0 && commonGroupHeaderRender) {
			return commonGroupHeaderRender(selectedItems);
		}
		if (customGroupHeaderRender) {
			return customGroupHeaderRender(data);
		}
		return null;
	};

	// New state for local pagination (for front-loaded data)
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [paginatedData, setPaginatedData] = useState(data);

	// Handle local pagination for front-loaded data
	useEffect(() => {
		if (!pagination && localSortedData) {
			const startIndex = (currentPage - 1) * pageSize;
			const endIndex = startIndex + pageSize;
			const newPaginatedData = localSortedData.slice(startIndex, endIndex);
			setPaginatedData(newPaginatedData);
		}
	}, [localSortedData, currentPage, pageSize, pagination]);

	// Update sortedData based on pagination
	const sortedData = pagination ? data : paginatedData;

	// Pagination handlers for local data
	const handleNextPage = () => {
		if (currentPage < Math.ceil(localSortedData.length / pageSize)) {
			setCurrentPage((prevPage) => prevPage + 1);
		}
	};

	const handlePreviousPage = () => {
		if (currentPage > 1) {
			setCurrentPage((prevPage) => prevPage - 1);
		}
	};

	//handle page change
	const handlePageChange = (newPage) => {
		setCurrentPage(newPage);
	};

	const handlePageSizeChange = (newPageSize) => {
		setPageSize(newPageSize);
		setCurrentPage(1);
	};

	const tableClassname = classname('ds-table__wrapper', additionalClasses, {
		'ds-table__wrapper--large': size === 'large'
	});

	const topBarClassname = classname('ds-table__top-bar', {
		'ds-table__top-bar--sticky': stickyOffset > 0,
		'ds-table__top-bar--collapsed': collapsed
	});

	// Body content: one clear branch per state (loading / empty / rows) — kept as a
	// named helper instead of a nested ternary inside the JSX.
	const renderTableBody = () => {
		if (loading) {
			return (
				<div className="loading">
					<img src={assetUrl("/loading.gif")} alt="Loading" />
				</div>
			);
		}
		if (sortedData.length === 0) {
			return (
				<div className="ds-table__row ds-table__empty-row">
					<div className="ds-table__cell ds-table__cell--empty">
						<Icon size={IconSize.MEDIUM} style={IconStyle.OUTLINED} name="information-circle" />
						<span>{emptyMessage}</span>
					</div>
				</div>
			);
		}
		return sortedData.map((row) => {
			const rowClassname = classname('ds-table__row', {
				'ds-table__row--selectable': isSelectable,
				'ds-table__row--selected': selectedItems.some((item) => item.id === row.id),
				'ds-table__row--disabled': row.disabled,
				'ds-table__row--link': row.link
			});

			return (
				<div key={row.id} className={rowClassname}>
					{isSelectable && <RenderCheckBoxCell isHeader={false} row={row} leftFreeze={leftFreeze} sortedData={sortedData} selectedItems={selectedItems} setSelectedItems={setSelectedItems} />}
					{headers.map((header, index) => {
						const id = uniqueId('cell-');

						return (
							<Cell
								key={id}
								header={header}
								index={index}
								row={row}
								isHeader={false}
								leftFreeze={leftFreeze}
								rightFreeze={rightFreeze}
								headers={headers}
								handleSort={handleSort}
							/>
						);
					})}
				</div>
			);
		});
	};

	return (
		<div className={tableClassname}>
			{showTopBar && (
				<div className={topBarClassname} ref={topBarRef} style={stickyOffset > 0 ? { top: `${stickyOffset}px` } : {}}>
					<div className="ds-table__collapse-button">
						{isCollapsible && (
							<Button
								type={ButtonType.UTILITY}
								variant={ButtonVariant.UTILITY}
								onClick={() => setCollapsed(!collapsed)}
								ref={collapseButtonRef}
								text={
									<Icon
										size={IconSize.LARGE}
										style={IconStyle.SOLID}
										name="chevron-up"
									/>
								}
							/>
						)}
					</div>
					<div className="ds-table__title">
						{selectedItems.length > 0 ? (
							<>
								{selectedItems.length} {customSelectedText}
							</>
						) : (
							title && (
								<Tooltip id="ds-table__title__tooltip" content={title} disabled={!isEllipsis[0]}>
									<div
										className="ds-table__title__text"
										style={{ maxWidth: titleMaxWidth ?? 0 }}
										ref={(el) => {
											titleRef.current = el;
											elementRefs.current[0] = el;
										}}
									>
										{title}
									</div>
								</Tooltip>
							)
						)}
					</div>

					{renderGroupHeader() && (
						<div className="ds-table__actions" ref={actionsRef}>
							{renderGroupHeader()}
						</div>
					)}
				</div>
			)}

			{!collapsed && (
				<ScrollSync>
					<div className="ds-table">
						{tableHelperText && <div className="ds-table__helper-text">{tableHelperText()}</div>}
						{informationHeaderRender && selectedItems.length > 0 && <div className="ds-table__information-header">{informationHeaderRender(selectedItems)}</div>}
						{/* Header */}
						<ScrollSyncPane>
							<div className={`ds-table__header-container ${stickyOffset > 0 ? 'ds-table__header-container--sticky' : ''}`} style={stickyOffset > 0 ? { top: `${headerTopOffset}px` } : {}}>
								<div className="ds-table__row ds-table__header">
									{isSelectable && <RenderCheckBoxCell isHeader={true} row={null} leftFreeze={leftFreeze} sortedData={sortedData} selectedItems={selectedItems} setSelectedItems={setSelectedItems} />}
									{headers.map((header, index) => {
										const id = uniqueId('header-cell-');
										return (
											<Cell
												key={id}
												header={header}
												index={index}
												row={null}
												isHeader={true}
												leftFreeze={leftFreeze}
												rightFreeze={rightFreeze}
												headers={headers}
												handleSort={handleSort}
												sortConfig={currentSortConfig}
											/>
										);
									})}
								</div>
							</div>
						</ScrollSyncPane>
						{/* Body */}
						<ScrollSyncPane>
							<div className="ds-table__body-container">
								{renderTableBody()}
							</div>
						</ScrollSyncPane>
						{/* Footer */}
						<div className="ds-table__footer-container">
							<PaginationControls pagination={pagination} data={data} pageSize={pageSize} currentPage={currentPage} handlePreviousPage={handlePreviousPage} handleNextPage={handleNextPage} handlePageSizeChange={handlePageSizeChange} handlePageChange={handlePageChange} paginationOptions={paginationOptions} />
						</div>
					</div>
				</ScrollSync>
			)}
		</div>
	);
};


export default Table;

RenderCheckBoxCell.propTypes = {
	isHeader: PropTypes.bool,
	leftFreeze: PropTypes.bool,
	setSelectedItems: PropTypes.func,
	sortedData: PropTypes.array,
	row: PropTypes.object,
	selectedItems: PropTypes.array,
}

Table.propTypes = {
	headers: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			label: PropTypes.string,
			width: PropTypes.string,
			render: PropTypes.func,
		})
	).isRequired,
	data: PropTypes.array,
	onSort: PropTypes.func,
	sortConfig: PropTypes.object,
	title: PropTypes.string,
	customGroupHeaderRender: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.element,
	]),
	commonGroupHeaderRender: PropTypes.func,
	isCollapsible: PropTypes.bool,
	stickyOffset: PropTypes.number,
	leftFreeze: PropTypes.bool,
	rightFreeze: PropTypes.bool,
	isSelectable: PropTypes.bool,
	customSelectedText: PropTypes.string,
	emptyMessage: PropTypes.string,
	pagination: PropTypes.object,
	size: PropTypes.string,
	loading: PropTypes.bool,
	additionalClasses: PropTypes.string,
	defaultSort: PropTypes.string,
	paginationOptions: PropTypes.array, 
	informationHeaderRender: PropTypes.func, 
	tableHelperText: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.string,
	]),
}